<template>
  <div class="max-w-(--breakpoint-md)" data-cy="feature-access-view">
    <lf-card class="divide-y">
      <div
        v-for="row in visibleRows"
        :key="row.key"
        class="relative py-6"
        :data-cy="row.dataCy"
      >
        <lf-switch
          :model-value="rowValues[row.key]"
          :name="`feature-access-${row.key}`"
          :disabled="pendingRowKeys.has(row.key)"
          :data-cy="`${row.dataCy}-toggle`"
          content-class="space-y-1"
          @toggle-changed="(newValue: boolean) => handleToggle(row, newValue)"
        >
          <lf-h3>{{ $t(row.titleI18n) }}</lf-h3>
          <span class="block text-gray-600">
            {{ $t(row.subtitleI18n) }}
          </span>
        </lf-switch>
        <div
          v-if="row.key === 'underwriting' && rowValues.underwriting"
          class="mt-6 rounded-md bg-gray-50 p-5"
          data-cy="feature-access-underwriting-funder"
        >
          <div class="flex items-center justify-between">
            <div class="flex flex-col">
              <lf-h3>{{ $t("ORGANIZATION.SELF_FUNDING") }}</lf-h3>
              <span class="text-gray-600">
                {{ $t("ORGANIZATION.SELF_FUNDING_SUB") }}
              </span>
            </div>
            <div class="w-64">
              <lf-dropdown-with-infinite-query
                :value="selectedFunder?.full_name"
                :placeholder="$t('ORGANIZATION.SELECT_FUNDER')"
                :search-term="$t('SIDEBAR.FUNDERS')"
                :query-options="approvedFundersQueryOptions"
                accessor="full_name"
                name="feature-access-funder"
                include-none
                data-cy="feature-access-funder-dropdown"
                @change="changeSelectedFunder"
              />
            </div>
          </div>
          <div class="mt-5 flex justify-end border-t pt-5">
            <primary-button
              :disabled="isSavingFunder"
              data-cy="feature-access-funder-save"
              @click="saveFunder"
            >
              {{ $t("ORGANIZATION.API_CONFIG.SAVE_CHANGES") }}
            </primary-button>
          </div>
        </div>
      </div>
    </lf-card>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import { useAuth } from "@/hooks/auth";
import { useClients } from "@/hooks/clients";
import { usePromiseWrapper } from "@/hooks/common";
import { useNotification } from "@/hooks/notifications";
import { FUNDERS_QUERY_KEYS } from "@/hooks/queries/funders";
import fundersApiService from "@/services/modules/funders";
import { buildFeatureAccessRows } from "@/views/profile/components/featureAccess/rows";

import LfDropdownWithInfiniteQuery from "@/components/ui/inputs/LfDropdownWithInfiniteQuery.vue";

import type { IClient } from "@/models/clients";
import type {
  FeatureAccessContext,
  FeatureAccessRow,
} from "@/models/featureAccess";
import type { IFunder } from "@/models/funders";

const { context = "clientSettings" } = defineProps<{
  context?: FeatureAccessContext;
}>();

const { t } = useI18n();
const { dispatch } = useStore();
const auth = useAuth();
const { activeClient } = useClients();
const { showMessage } = useNotification();

const approvedFundersQueryOptions = {
  queryKey: [FUNDERS_QUERY_KEYS.LIST, "approved"],
  queryFn: ({ page, search }: { page: number; search?: string }) =>
    fundersApiService.getFunders({
      approved: 1,
      page,
      ...(search && { search }),
    }),
};

const pendingRowKeys = ref<Set<string>>(new Set());
const selectedFunder = ref<IFunder | null>(
  activeClient.value?.linked_funder ?? null
);
const rowValues = reactive<Record<string, boolean>>({});

const roleIsAnalyst = computed(() => auth.isAnalyst || auth.isClientAnalyst);

const visibleRows = computed<FeatureAccessRow[]>(() =>
  buildFeatureAccessRows({
    auth,
    client: activeClient.value,
    context,
    roleIsAnalyst: roleIsAnalyst.value,
  }).filter((row) => row.visible)
);

const syncRowValuesFromClient = (client: IClient | null) => {
  for (const row of visibleRows.value) {
    rowValues[row.key] = !!client?.[row.field];
  }
};

const handleToggle = async (row: FeatureAccessRow, newValue: boolean) => {
  if (pendingRowKeys.value.has(row.key)) {
    return;
  }

  const previousValue = rowValues[row.key];
  rowValues[row.key] = newValue;
  pendingRowKeys.value.add(row.key);

  try {
    const result = await dispatch("clients/updateClient", {
      data: { [row.field]: newValue },
    });

    if (!result) {
      rowValues[row.key] = previousValue;
      showMessage(t("COMMON.ERROR_OCCURRED"), "error");

      return;
    }

    if (row.afterSaveAction) {
      await dispatch(row.afterSaveAction);
    }
  } catch {
    rowValues[row.key] = previousValue;
    showMessage(t("COMMON.ERROR_OCCURRED"), "error");
  } finally {
    pendingRowKeys.value.delete(row.key);
  }
};

const changeSelectedFunder = (funder: IFunder | null | undefined) => {
  selectedFunder.value = funder ?? null;
};

const { fetchWrapper: saveFunder, loading: isSavingFunder } = usePromiseWrapper(
  async () => {
    const funderId = selectedFunder.value?.id ?? null;

    await dispatch("clients/storeLinkedFunder", funderId);
  },
  {
    onError: () => {
      showMessage(t("COMMON.ERROR_OCCURRED"), "error");
      selectedFunder.value = activeClient.value?.linked_funder ?? null;
    },
  }
);

watch(
  activeClient,
  (client) => {
    syncRowValuesFromClient(client);
    selectedFunder.value = client?.linked_funder ?? null;
  },
  { immediate: true }
);
</script>
